import { useCallback, useMemo } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Avatar, Typography } from '@mui/material';
import { format } from 'date-fns';
import ListHeader from '@/components/ListHeader';
import Menu from '@/components/Menu';
import { getEndpointWithParams } from '@/routes/config';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { InterviewTechnicalChallenge } from '@/types/api';
import { InterviewChallengeRequestStatusFilter } from '@/types/api/interviewTechnicalChallenge';
import { Paths } from '@/types/routes';
import { fullDateWithHours } from '@/utils/checkins';
import AsignedReviewersList from './AsignedReviewersList';
import * as Styles from './ChallengeManagement.styles';

type ChallengeManagementComponentProps = PaginationTypes & {
  challenges: InterviewTechnicalChallenge[];
  isLoading: boolean;
  statusFilter: InterviewChallengeRequestStatusFilter;
  onStatusFilterChange: (status: InterviewChallengeRequestStatusFilter) => void;
  onEditChallenge?: (challenge: InterviewTechnicalChallenge) => void;
};

const ChallengeManagementComponent = ({
  challenges,
  isLoading,
  currentPage,
  itemsPerPage,
  count,
  lastPage,
  onChangePageHandler,
  onChangeRecordsPerPage,
  statusFilter,
  onStatusFilterChange,
  onEditChallenge,
}: ChallengeManagementComponentProps): JSX.Element => {
  const renderHeader = useMemo(() => {
    if (!isLoading && !challenges.length) {
      return <></>;
    }

    return (
      <Tr>
        <Th>Candidate Name</Th>
        <Th>Challenge Name</Th>
        <Th>Reviewers</Th>
        <Th>Gitlab URL</Th>
        <Th>Created Time</Th>
        <Th>Actions</Th>
      </Tr>
    );
  }, [isLoading, challenges]);

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle width={6} height={6} className="tw-mr-3" />
          <Skeleton className="tw-h-3.5 tw-w-[100px]" />
        </div>
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle width={7} height={7} className="tw-mr-2" />
          <SkeletonCircle width={7} height={7} className="tw-mr-2" />
          <SkeletonCircle width={7} height={7} />
        </div>
      </Td>
      <Td textAlign="center">
        <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9', width: 12, height: 12 }} />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td textAlign="center">
        <Skeleton className="tw-h-3.5 tw-w-[24px]" />
      </Td>
    </Tr>
  );

  const renderRow = useCallback(
    (challenge: InterviewTechnicalChallenge) => {
      const createdAt = format(new Date(challenge.createdAt), fullDateWithHours);
      const person = challenge.interview.person || {};

      const candidateLink = getEndpointWithParams(Paths.CANDIDATE_INTERVIEWS, {
        id: person.id,
      });

      const gitProjectLink = challenge.gitlabProjectUrl || challenge.githubProjectUrl;

      return (
        <Tr key={challenge.id}>
          <Td>
            <Styles.Centered>
              <Avatar
                src={person.pictureUrl || ''}
                alt={`${person.firstName} ${person.lastName || ''} profile`}
                sx={{ width: 24, height: 24, marginRight: 1 }}
              >
                <Typography variant="caption">
                  {person.firstName?.charAt(0) || ''}
                  {person.lastName?.charAt(0) || ''}
                </Typography>
              </Avatar>
              <Styles.TableLink to={candidateLink} className="capitalize">
                {person.firstName} {person.lastName}
              </Styles.TableLink>
            </Styles.Centered>
          </Td>

          <Td>{challenge.technicalChallenge?.name || 'N/A'}</Td>

          <Td>
            <AsignedReviewersList reviewers={challenge.reviewers} />
          </Td>

          <Td textAlign="center">
            {gitProjectLink ? (
              <a
                aria-label="Open GitLab project link"
                href={gitProjectLink}
                rel="noreferrer"
                target="_blank"
              >
                <OpenInNewOutlinedIcon sx={{ color: '#054ad9', width: 12, height: 12 }} />
              </a>
            ) : (
              <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9', width: 12, height: 12 }} />
            )}
          </Td>

          <Td>{createdAt}</Td>
          <Td textAlign="center">
            <Menu
              icon={MoreHorizIcon}
              menuId={`challenge-actions-${challenge.id}`}
              menuItems={[
                {
                  label: 'Edit',
                  menuItemIcon: EditIcon,
                  callback: () => {
                    onEditChallenge?.(challenge);
                  },
                },
              ]}
            />
          </Td>
        </Tr>
      );
    },
    [onEditChallenge]
  );

  return (
    <Styles.Content data-testid="challenges-list">
      <ListHeader
        title="Challenges"
        showActionsArea
        showSearchBar={false}
        isCandidatesSearchBar={false}
        showLinks={false}
      />

      <Styles.StatusFilterContainer>
        <Styles.StatusFilterButton
          isActive={statusFilter === InterviewChallengeRequestStatusFilter.UNREVIEWED}
          onClick={() => onStatusFilterChange(InterviewChallengeRequestStatusFilter.UNREVIEWED)}
        >
          Unreviewed
        </Styles.StatusFilterButton>
        <Styles.StatusFilterButton
          isActive={statusFilter === InterviewChallengeRequestStatusFilter.NOT_SUBMITTED}
          onClick={() => onStatusFilterChange(InterviewChallengeRequestStatusFilter.NOT_SUBMITTED)}
        >
          Not Submitted
        </Styles.StatusFilterButton>
        <Styles.StatusFilterButton
          isActive={statusFilter === InterviewChallengeRequestStatusFilter.ALL}
          onClick={() => onStatusFilterChange(InterviewChallengeRequestStatusFilter.ALL)}
        >
          All
        </Styles.StatusFilterButton>
      </Styles.StatusFilterContainer>

      {!challenges.length && !isLoading && (
        <Styles.NotFound>
          <Styles.NotFoundSubtitle>No challenges found</Styles.NotFoundSubtitle>
          <p>No technical challenges found.</p>
        </Styles.NotFound>
      )}

      {isLoading ? (
        <Table
          renderHeader={renderHeader}
          renderRow={renderEmptyStateRows}
          data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
        />
      ) : (
        <Table renderHeader={renderHeader} renderRow={renderRow} data={challenges} />
      )}

      <Pagination
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        lastPage={lastPage}
        itemsPerPage={itemsPerPage}
        count={count}
        currentPage={currentPage}
        onChangePageHandler={onChangePageHandler}
      />
    </Styles.Content>
  );
};

export default ChallengeManagementComponent;
